import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { toCalendarDate } from '../../../utils/helpers/aiSpendFormat';

/**
 * The filter state for the whole AI spend area lives in the URL query string.
 *
 * It is the cache key, the share link ("look at this spike"), and the back
 * button, all for free. A store would need two-way syncing with the URL, which
 * is strictly more code and one more thing to get out of step.
 *
 * Multi-values are comma-joined (`?provider=openai,heygen`): axios serialises
 * arrays as `provider[]=`, which DRF's getlist does not see -- and a filter the
 * server cannot see is a page that silently shows unfiltered totals.
 */

export const DEFAULT_RANGE_DAYS = 30;
export const DEFAULT_PAGE_SIZE = 25;

const MULTI_KEYS = ['provider', 'service', 'feature', 'model'];

export const defaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (DEFAULT_RANGE_DAYS - 1));
  // Local calendar parts throughout: toISOString() would shift the day for
  // anyone east of UTC, which is everyone using this product.
  return { date_from: toCalendarDate(from), date_to: toCalendarDate(to) };
};

const readList = (searchParams, key) => {
  const raw = searchParams.get(key);
  return raw ? raw.split(',').map((part) => part.trim()).filter(Boolean) : [];
};

const readInt = (searchParams, key, fallback) => {
  const value = Number.parseInt(searchParams.get(key), 10);
  return Number.isFinite(value) && value > 0 ? value : fallback;
};

export const parseAiSpendFilters = (searchParams) => {
  const range = defaultRange();
  return {
    date_from: searchParams.get('date_from') || range.date_from,
    date_to: searchParams.get('date_to') || range.date_to,
    provider: readList(searchParams, 'provider'),
    service: readList(searchParams, 'service'),
    feature: readList(searchParams, 'feature'),
    model: readList(searchParams, 'model'),
    organization: searchParams.get('organization') || '',
    subject: searchParams.get('subject') || '',
    initiator: searchParams.get('initiator') || '',
    session: searchParams.get('session') || '',
    status: searchParams.get('status') || 'all',
    search: searchParams.get('search') || '',
    bucket: searchParams.get('bucket') || 'day',
    group_by: searchParams.get('group_by') || 'none',
    metric: searchParams.get('metric') || 'cost',
    ordering: searchParams.get('ordering') || '',
    page: readInt(searchParams, 'page', 1),
    page_size: readInt(searchParams, 'page_size', DEFAULT_PAGE_SIZE),
    run: searchParams.get('run') || '',
    target: searchParams.get('target') || '',
  };
};

const writeValue = (next, key, value) => {
  if (value === null || value === undefined || value === '' ||
      (Array.isArray(value) && value.length === 0)) {
    next.delete(key);
    return;
  }
  next.set(key, Array.isArray(value) ? value.join(',') : String(value));
};

export const applyAiSpendPatch = (current, patch, { keepPage = false } = {}) => {
  const next = new URLSearchParams(current);
  Object.entries(patch).forEach(([key, value]) => writeValue(next, key, value));
  // Any filter change invalidates the page you were on -- the same idiom the
  // Universities screen already uses.
  const onlyPaging = Object.keys(patch).every((key) =>
    ['page', 'page_size', 'run'].includes(key),
  );
  if (!keepPage && !onlyPaging) next.delete('page');
  return next;
};

/**
 * The params actually sent to the API. `status` is a UI concept; the API knows
 * only `success`, and it rejects parameter names it does not recognise -- so
 * the mapping happens here rather than being sent and silently ignored.
 */
export const toAiSpendQueryParams = (filters, { include = null } = {}) => {
  const params = {
    date_from: filters.date_from,
    date_to: filters.date_to,
  };
  MULTI_KEYS.forEach((key) => {
    if (filters[key]?.length) params[key] = filters[key].join(',');
  });
  ['organization', 'subject', 'initiator', 'session', 'search'].forEach((key) => {
    if (filters[key]) params[key] = filters[key];
  });
  if (filters.status === 'failed') params.success = false;
  if (filters.status === 'succeeded') params.success = true;
  if (include) Object.assign(params, include);
  return params;
};

export const countActiveFilters = (filters) =>
  MULTI_KEYS.reduce((total, key) => total + (filters[key]?.length ? 1 : 0), 0) +
  ['organization', 'subject', 'initiator', 'session', 'search'].filter(
    (key) => filters[key],
  ).length +
  (filters.status !== 'all' ? 1 : 0);

export default function useAiSpendFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => parseAiSpendFilters(searchParams), [searchParams]);

  const setFilters = useCallback(
    (patch, { replace = false, keepPage = false } = {}) => {
      setSearchParams((current) => applyAiSpendPatch(current, patch, { keepPage }), {
        replace,
      });
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setFilters({
      provider: [], service: [], feature: [], model: [],
      organization: '', subject: '', initiator: '', session: '',
      status: 'all', search: '',
    });
  }, [setFilters]);

  const queryParams = useMemo(() => toAiSpendQueryParams(filters), [filters]);
  // A single stable string, so every page's fetch dependency array is one item.
  const queryKey = searchParams.toString();
  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);

  return { filters, setFilters, clearFilters, queryParams, queryKey, activeFilterCount };
}
